import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  TrendingUp, Clock, AlertTriangle,
  DollarSign, ShoppingCart, ArrowUpRight, Truck, Timer, CalendarClock
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const CHART_COLORS = ['#18181B', '#3B82F6', '#52525B', '#A1A1AA', '#D4D4D8', '#F59E0B', '#10B981', '#EF4444'];
const STATUS_COLOR_MAP = {
  UNISSUED: '#A1A1AA', ISSUED: '#3B82F6', PARTIAL: '#F59E0B',
  COMPLETED: '#10B981', RECEIVED: '#10B981', CANCELLED: '#EF4444',
  SUBMITTED: '#6366F1', PENDING_APPROVAL: '#8B5CF6', ACKNOWLEDGED: '#6366F1',
  AMENDED: '#F97316', CLOSED: '#64748B',
};

const fmt$ = (v) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
};
const fmtFull = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function PODashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/orders/dashboard/summary')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const drillTo = (params) => {
    const sp = new URLSearchParams({ tab: 'orders', ...params });
    navigate(`/orders?${sp.toString()}`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin h-6 w-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full" />
    </div>
  );
  if (!data) return <div className="text-center py-16 text-zinc-400">Dashboard unavailable</div>;

  const k = data.kpis;
  const pctChange = k.spend_prev_month ? Math.round(((k.spend_mtd - k.spend_prev_month) / k.spend_prev_month) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="po-dashboard">
      <KPIStrip kpis={k} pctChange={pctChange} drillTo={drillTo} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <OperationalRisks risks={data.operational_risks} navigate={navigate} drillTo={drillTo} />
        </div>
        <StatusBreakdown data={data.by_status} drillTo={drillTo} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SpendTrend data={data.spend_trend} />
        </div>
        <SpendByCategory data={data.by_category} drillTo={drillTo} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopSuppliers data={data.top_suppliers} drillTo={drillTo} />
        <SupplierPerformance perf={data.supplier_performance} drillTo={drillTo} />
      </div>
      <ProcessEfficiency eff={data.process_efficiency} drillTo={drillTo} />
    </div>
  );
}

/* ────────────────────── KPI STRIP ────────────────────── */
function KPIStrip({ kpis: k, pctChange, drillTo }) {
  const cards = [
    { label: 'Open POs', value: k.total_open_count, icon: ShoppingCart, testid: 'kpi-open-po-count',
      onClick: () => drillTo({ status: 'ISSUED,PARTIAL,UNISSUED,SUBMITTED', label: 'Open POs' }) },
    { label: 'Open PO Value', value: fmtFull(k.total_open_value), icon: DollarSign, testid: 'kpi-open-po-value',
      onClick: () => drillTo({ status: 'ISSUED,PARTIAL,UNISSUED,SUBMITTED', label: 'Open PO Value' }) },
    { label: 'Spend MTD', value: fmt$(k.spend_mtd), icon: TrendingUp, testid: 'kpi-spend-mtd',
      sub: pctChange !== 0 ? `${pctChange > 0 ? '+' : ''}${pctChange}% vs prev month` : null,
      subColor: pctChange >= 0 ? 'text-emerald-600' : 'text-red-600',
      onClick: () => drillTo({ label: 'This Month Orders' }) },
    { label: 'Pending Approvals', value: k.pending_approvals, icon: Clock, warn: k.pending_approvals > 0, testid: 'kpi-pending-approvals',
      onClick: () => drillTo({ status: 'UNISSUED', label: 'Pending Approvals' }) },
    { label: 'Overdue POs', value: k.overdue_count, sub: fmt$(k.overdue_value), icon: AlertTriangle, warn: k.overdue_count > 0, testid: 'kpi-overdue',
      onClick: () => drillTo({ overdue: 'true', label: 'Overdue POs' }) },
    { label: 'Avg Cycle Time', value: `${k.avg_cycle_days}d`, icon: Timer, testid: 'kpi-cycle-time',
      onClick: () => drillTo({ status: 'COMPLETED,RECEIVED', label: 'Completed / Received POs' }) },
  ];

  return (
    <div className="flex flex-col md:flex-row flex-wrap lg:flex-nowrap border border-zinc-200 rounded-md overflow-hidden shadow-sm" data-testid="kpi-strip">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i}
            onClick={c.onClick}
            className={`flex-1 min-w-[160px] p-4 cursor-pointer transition-all hover:bg-zinc-50 active:bg-zinc-100 group ${i > 0 ? 'border-t md:border-t-0 md:border-l' : ''} border-zinc-200 bg-white`}
            data-testid={c.testid}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Icon className={`h-3.5 w-3.5 ${c.warn ? 'text-red-500' : 'text-zinc-400'}`} strokeWidth={1.5} />
                <span className="text-[11px] font-semibold tracking-wide uppercase text-zinc-500">{c.label}</span>
              </div>
              <ArrowUpRight className="h-3 w-3 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className={`text-xl font-semibold tracking-tight ${c.warn ? 'text-red-600' : 'text-zinc-900'}`}>{c.value}</p>
            {c.sub && <p className={`text-[11px] mt-0.5 ${c.subColor || 'text-zinc-400'}`}>{c.sub}</p>}
          </div>
        );
      })}
    </div>
  );
}

/* ────────────────────── OPERATIONAL RISKS ────────────────────── */
function OperationalRisks({ risks, navigate, drillTo }) {
  const { approval_aging, overdue_orders, due_next_7d, high_value_pending } = risks;
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="operational-risks">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Operational Risks & Actions</h3>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Pending Approvals by Age</p>
          <div className="flex gap-3">
            {[
              { label: '0-3 days', count: approval_aging["0_3"], color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' },
              { label: '4-7 days', count: approval_aging["4_7"], color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100' },
              { label: '7+ days', count: approval_aging["7_plus"], color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
            ].map(b => (
              <div key={b.label}
                onClick={() => drillTo({ status: 'UNISSUED', label: `Approvals ${b.label}` })}
                className={`flex-1 border rounded-md p-3 text-center cursor-pointer transition-colors ${b.color}`}
                data-testid={`aging-${b.label.replace(/\s/g, '-')}`}>
                <p className="text-lg font-bold">{b.count}</p>
                <p className="text-[10px] font-medium uppercase tracking-wide">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RiskList title="Overdue POs" items={overdue_orders} badge="bg-red-100 text-red-700" onDrill={navigate}
            onTitleClick={() => drillTo({ overdue: 'true', label: 'Overdue POs' })} />
          <RiskList title="Due Next 7 Days" items={due_next_7d} badge="bg-amber-100 text-amber-700" onDrill={navigate}
            onTitleClick={() => drillTo({ label: 'Due Next 7 Days' })} />
        </div>
        {high_value_pending.length > 0 && (
          <div className="pt-4 mt-2 border-t border-zinc-200">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1">
              High-Value Pending Approval ({'>'}$10K)
              <button onClick={() => drillTo({ status: 'UNISSUED', label: 'High-Value Pending' })}
                className="text-blue-600 hover:underline ml-1">View all</button>
            </p>
            <div className="space-y-1">
              {high_value_pending.slice(0, 5).map(o => (
                <div key={o.order_number}
                  className="grid grid-cols-[100px_180px_1fr_120px_70px] items-center gap-3 text-sm px-3 py-2 rounded hover:bg-zinc-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/orders/${o.order_number}`)}
                  data-testid={`high-value-${o.order_number}`}>
                  <span className="font-mono text-primary font-semibold text-left">{o.order_number}</span>
                  <span className="text-zinc-700 text-xs truncate text-left">{o.supplier_name}</span>
                  <span className="text-zinc-500 text-xs truncate text-left italic">{o.description || '—'}</span>
                  <span className="font-semibold text-right tabular-nums">{fmtFull(o.value)}</span>
                  <Badge className="bg-zinc-100 text-zinc-600 text-[9px] justify-self-end">{o.age_days}d old</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RiskList({ title, items, badge, onDrill, onTitleClick }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-1.5 flex items-center gap-1">
        <span className="cursor-pointer hover:text-zinc-700" onClick={onTitleClick}>{title}</span>
        <Badge className={`${badge} text-[9px] ml-1 cursor-pointer`} onClick={onTitleClick}>{items.length}</Badge>
      </p>
      {items.length === 0 ? <p className="text-xs text-zinc-400 italic">None</p> : (
        <div className="space-y-0.5 max-h-32 overflow-y-auto">
          {items.slice(0, 5).map(o => (
            <div key={o.order_number}
              className="grid grid-cols-[90px_1fr_auto] items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-zinc-50 cursor-pointer transition-colors"
              onClick={() => onDrill(`/orders/${o.order_number}`)}
              data-testid={`risk-${o.order_number}`}>
              <span className="font-mono text-primary font-medium text-left">{o.order_number}</span>
              <span className="text-zinc-500 truncate text-left">{o.supplier_name}</span>
              <span className="font-semibold whitespace-nowrap text-right tabular-nums">{fmt$(o.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────── STATUS BREAKDOWN (Donut) ────────────────────── */
function StatusBreakdown({ data, drillTo }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const chartData = data.map(d => ({ ...d, fill: STATUS_COLOR_MAP[d.status] || '#D4D4D8' }));

  const handleClick = (entry) => {
    if (entry && entry.status) {
      drillTo({ status: entry.status, label: `Status: ${entry.status}` });
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="status-breakdown">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">PO Status Breakdown</h3>
      </div>
      <div className="p-5 flex flex-col items-center">
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie data={chartData} dataKey="count" nameKey="status" cx="50%" cy="50%"
              innerRadius={50} outerRadius={75} strokeWidth={2} stroke="#fff"
              onClick={handleClick} cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(val, name) => [`${val} POs`, name]} />
          </PieChart>
        </ResponsiveContainer>
        <div className="text-center -mt-4 mb-3">
          <p className="text-2xl font-bold text-zinc-900">{total}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Total POs</p>
        </div>
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
          {chartData.map(d => (
            <div key={d.status}
              className="flex items-center gap-1.5 text-xs cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => handleClick(d)}
              data-testid={`status-legend-${d.status}`}>
              <div className="h-2.5 w-2.5 rounded-full" style={{ background: d.fill }} />
              <span className="text-zinc-600">{d.status}</span>
              <span className="font-semibold text-zinc-900">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────── SPEND TREND (Line) ────────────────────── */
function SpendTrend({ data }) {
  const chartData = data.map(d => ({ ...d, label: d.month.slice(5), value: Math.round(d.value) }));
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="spend-trend">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">Spend Over Time</h3>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wide">Last 12 months</span>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={v => fmt$(v)} tick={{ fontSize: 11, fill: '#71717a' }} axisLine={false} tickLine={false} width={60} />
            <Tooltip formatter={(v) => [fmtFull(v), 'Spend']} labelStyle={{ color: '#18181b', fontWeight: 600 }} />
            <Line type="monotone" dataKey="value" stroke="#18181B" strokeWidth={2}
              dot={{ r: 3, fill: '#18181B' }} activeDot={{ r: 6, cursor: 'pointer' }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── SPEND BY CATEGORY (Pie) ────────────────────── */
function SpendByCategory({ data, drillTo }) {
  const chartData = data.map((d, i) => ({
    ...d,
    name: d.category.length > 14 ? d.category.slice(0, 12) + '...' : d.category,
    fullName: d.category,
    fill: CHART_COLORS[i % CHART_COLORS.length]
  }));

  const handleClick = (entry) => {
    if (entry && entry.fullName) {
      drillTo({ category: entry.fullName, label: `Category: ${entry.fullName}` });
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="spend-by-category">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Spend by Category</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}
              strokeWidth={2} stroke="#fff"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}
              onClick={handleClick} cursor="pointer">
              {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={(v) => [fmtFull(v), 'Spend']} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── TOP SUPPLIERS (Bar) ────────────────────── */
function TopSuppliers({ data, drillTo }) {
  const chartData = data.map(d => ({
    ...d,
    name: d.supplier.length > 16 ? d.supplier.slice(0, 14) + '...' : d.supplier,
    fullName: d.supplier
  }));

  const handleClick = (entry) => {
    if (entry && entry.fullName) {
      drillTo({ supplier: entry.fullName, label: `Supplier: ${entry.fullName}` });
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="top-suppliers">
      <div className="px-5 py-3.5 border-b border-zinc-200">
        <h3 className="text-sm font-semibold text-zinc-900">Top 10 Suppliers by Spend</h3>
      </div>
      <div className="p-5">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" horizontal={false} />
            <XAxis type="number" tickFormatter={v => fmt$(v)} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: '#52525b' }} width={110} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => [fmtFull(v), 'Total Spend']} />
            <Bar dataKey="value" fill="#18181B" radius={[0, 4, 4, 0]} barSize={16}
              onClick={handleClick} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ────────────────────── SUPPLIER PERFORMANCE ────────────────────── */
function SupplierPerformance({ perf, drillTo }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="supplier-performance">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <Truck className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Supplier Performance</h3>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-3 gap-4 mb-5">
          <div className="text-center p-3 border border-zinc-100 rounded-md">
            <p className={`text-2xl font-bold ${perf.on_time_pct >= 80 ? 'text-emerald-600' : perf.on_time_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{perf.on_time_pct}%</p>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">On-Time</p>
          </div>
          <div className="text-center p-3 border border-zinc-100 rounded-md">
            <p className="text-2xl font-bold text-zinc-900">{perf.avg_delay_days}d</p>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Avg Delay</p>
          </div>
          <div className="text-center p-3 border border-zinc-100 rounded-md">
            <p className="text-2xl font-bold text-zinc-900">{perf.total_deliveries}</p>
            <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-0.5">Deliveries</p>
          </div>
        </div>
        {perf.worst_suppliers.length > 0 ? (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">Highest Delay Suppliers</p>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-zinc-200">
                <th className="pb-2 text-left font-medium text-zinc-500">Supplier</th>
                <th className="pb-2 text-right font-medium text-zinc-500">Late</th>
                <th className="pb-2 text-right font-medium text-zinc-500">Avg Delay</th>
                <th className="pb-2 text-right font-medium text-zinc-500">On-Time %</th>
              </tr></thead>
              <tbody>
                {perf.worst_suppliers.map(s => (
                  <tr key={s.supplier}
                    className="border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 transition-colors"
                    onClick={() => drillTo({ supplier: s.supplier, label: `Supplier: ${s.supplier}` })}
                    data-testid={`perf-supplier-${s.supplier}`}>
                    <td className="py-2 text-primary font-medium hover:underline">{s.supplier}</td>
                    <td className="py-2 text-right text-red-600 font-semibold">{s.late_count}</td>
                    <td className="py-2 text-right">{s.avg_delay}d</td>
                    <td className="py-2 text-right">
                      <Badge className={`text-[9px] ${s.on_time_pct >= 80 ? 'bg-emerald-100 text-emerald-700' : s.on_time_pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{s.on_time_pct}%</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : <p className="text-xs text-zinc-400 italic text-center py-4">No delivery data available yet</p>}
      </div>
    </div>
  );
}

/* ────────────────────── PROCESS EFFICIENCY ────────────────────── */
function ProcessEfficiency({ eff, drillTo }) {
  const stageStatusMap = {
    'Draft / Unissued': 'UNISSUED',
    'Issued / Active': 'ISSUED',
    'Partial Delivery': 'PARTIAL',
    'Completed': 'COMPLETED,RECEIVED',
    'Cancelled': 'CANCELLED',
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md shadow-sm" data-testid="process-efficiency">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-zinc-400" strokeWidth={1.5} />
        <h3 className="text-sm font-semibold text-zinc-900">Process Efficiency</h3>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-4">
            <div className="border border-zinc-100 rounded-md p-4 text-center">
              <p className="text-3xl font-bold text-zinc-900">{eff.avg_approval_days}<span className="text-sm font-normal text-zinc-400 ml-1">days</span></p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-1">Avg Approval Time</p>
            </div>
            <div className="border border-zinc-100 rounded-md p-4 text-center">
              <p className="text-3xl font-bold text-zinc-900">{eff.avg_cycle_days}<span className="text-sm font-normal text-zinc-400 ml-1">days</span></p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wide mt-1">Avg PO Cycle</p>
            </div>
          </div>
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">POs by Stage</p>
            <div className="space-y-2">
              {eff.by_stage.map((s, i) => {
                const maxCount = Math.max(...eff.by_stage.map(x => x.count), 1);
                const pct = Math.round((s.count / maxCount) * 100);
                return (
                  <div key={s.stage}
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => {
                      const mappedStatus = stageStatusMap[s.stage];
                      if (mappedStatus) drillTo({ status: mappedStatus, label: s.stage });
                    }}
                    data-testid={`stage-${s.stage.replace(/\s/g, '-')}`}>
                    <span className="text-xs text-zinc-600 w-32 shrink-0 text-right group-hover:text-primary group-hover:underline transition-colors">{s.stage}</span>
                    <div className="flex-1 h-5 bg-zinc-100 rounded overflow-hidden">
                      <div className="h-full rounded transition-all duration-500 group-hover:opacity-80"
                        style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    </div>
                    <span className="text-xs font-semibold text-zinc-900 w-8 text-right">{s.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
